import { useEffect, useState } from 'react';
import { ApiConsumer, ACRConsumer } from 'api/ApiConsumer';
import { API_ROUTES } from 'routes/api';
import { getTrackData } from 'api/util';

function sanitizeArtist(artist) {
  if (!artist || artist.trim() === '-') return '';
  return artist.trim();
}

export async function fetchCoverArtUrl(artist, title) {
  const cleanArtist = sanitizeArtist(artist);
  if (!cleanArtist) return null;

  try {
    const res = await ApiConsumer.get(API_ROUTES.LIBRARY.COVER_ART, {
      params: { artist: cleanArtist, title },
    });
    if (res?.data?.cover_small) return res.data.cover_small;
  } catch (_) {}

  try {
    const res = await ACRConsumer.get('/tracks', {
      params: { query: `artist: ${cleanArtist}, title: ${title}` },
    });
    const acrData = res?.data?.data?.pop();
    const { albumCover } = getTrackData(acrData);
    if (albumCover) return albumCover;
  } catch (_) {}

  return null;
}

export default function useCoverArt(artist, title) {
  const [coverArt, setCoverArt] = useState(null);
  const [metadata, setMetadata] = useState(null);

  const cleanArtist = sanitizeArtist(artist);

  useEffect(() => {
    if (!title || !cleanArtist) return;

    let cancelled = false;

    async function fetchCoverArt() {
      try {
        const res = await ApiConsumer.get(API_ROUTES.LIBRARY.COVER_ART, {
          params: { artist: cleanArtist, title },
        });
        const data = res?.data;

        if (!cancelled && data?.cover_medium) {
          setCoverArt(data.cover_medium);
          setMetadata({
            album: {
              name: data.album,
              cover: data.cover_medium,
              covers: {
                small: data.cover_small,
                medium: data.cover_medium,
                large: data.cover_big,
              },
            },
            artists: [{ name: data.artist }],
          });
          return;
        }
      } catch (_) {}

      try {
        const res = await ACRConsumer.get('/tracks', {
          params: { query: `artist: ${cleanArtist}, title: ${title}` },
        });
        const acrData = res?.data?.data?.pop();
        if (!cancelled && acrData) {
          const { albumCover } = getTrackData(acrData);
          if (albumCover) setCoverArt(albumCover);
          setMetadata(acrData);
        }
      } catch (_) {}
    }

    fetchCoverArt();
    return () => { cancelled = true; };
  }, [cleanArtist, title]);

  const parsed = getTrackData(metadata);
  return { coverArt, metadata, ...parsed };
}
